import React from 'react';

/**
 * How long a page has to keep loading before screen readers hear about it.
 * Quick loads stay quiet. On a first visit to the site it also means the
 * status region is already on the page when its text changes, rather than
 * arriving in the same commit as the first skeleton.
 */
const ANNOUNCE_DELAY_MS = 500;

/** Marks a page as loading, and returns the function that unmarks it. */
type MarkLoading = () => () => void;

const RouteLoadingContext = React.createContext<MarkLoading>(() => () => {});

/**
 * Tells screen readers "Loading…" while a page's code loads, from one status
 * region that stays on the page. Each route skeleton reports itself here
 * instead of announcing on its own. VoiceOver doesn't read a status region
 * that arrives with its text already inside, and every Suspense fallback is
 * new DOM. Handing off from one boundary's skeleton to a nested boundary's
 * happens in a single commit, so the loading count never drops to zero and
 * the announcement is made once.
 *
 * That relies on React running the old skeleton's effect cleanup and the new
 * one's effect in the same batched flush. The nested-Suspense handoff test in
 * `route-loading-announcer.test.tsx` fails if a React upgrade changes that.
 */
export default function RouteLoadingAnnouncer({
  children,
}: {
  children: React.ReactNode;
}) {
  const [loadingCount, setLoadingCount] = React.useState(0);
  const [announced, setAnnounced] = React.useState(false);
  const isLoading = loadingCount > 0;

  const markLoading = React.useCallback<MarkLoading>(() => {
    setLoadingCount((count) => count + 1);
    return () => setLoadingCount((count) => count - 1);
  }, []);

  React.useEffect(() => {
    if (!isLoading) {
      return;
    }
    const timer = setTimeout(() => setAnnounced(true), ANNOUNCE_DELAY_MS);
    return () => {
      clearTimeout(timer);
      setAnnounced(false);
    };
  }, [isLoading]);

  return (
    <RouteLoadingContext.Provider value={markLoading}>
      <div role="status" className="sr-only">
        {isLoading && announced ? 'Loading…' : ''}
      </div>
      {children}
    </RouteLoadingContext.Provider>
  );
}

/**
 * Counts the calling component's page as loading for as long as it's mounted.
 * Outside a `RouteLoadingAnnouncer` it does nothing.
 */
export function useMarkRouteLoading(): void {
  const markLoading = React.useContext(RouteLoadingContext);
  React.useEffect(() => markLoading(), [markLoading]);
}
